import { appHref } from './globals.js';

/**
 * Registers the offline app shell (sw.ts) and owns the update prompt.
 * ADR-0022 has the reasoning; the two things worth knowing here:
 *
 *  - Both the script path and the scope come from `globals().base`, which is
 *    injected from the `X-Colplat-Base` header. Nothing here hardcodes a
 *    mount path (ADR-0015). Scope equals the mount root and the script sits
 *    in it, so no `Service-Worker-Allowed` header is needed.
 *  - A waiting worker is never activated behind the user's back. This app
 *    holds leases, half-typed split tables and an outbox; reloading to
 *    deliver a cosmetic update would throw live work away.
 */

let waitingWorker: ServiceWorker | undefined;

/**
 * How long after the page loaded an update may still be taken silently.
 *
 * A new worker usually finishes installing a second or two after the page it
 * is replacing has drawn. Ten seconds is generous enough to cover a slow
 * network and short enough that "the user just arrived" is still true.
 */
const SILENT_UPDATE_WINDOW_MS = 10_000;

const loadedAt = Date.now();
let interacted = false;

/**
 * How many times this tab has reloaded itself to take an update, recently.
 *
 * A reload that does not fix the thing it was for is a loop, and a loop
 * costs the user the app entirely. The one that happened: `schemaStale`
 * calls `forceUpdateNow`, which with no worker waiting falls back to a plain
 * reload — and a cache-first shell answers that with the very bundle the
 * server just refused, which asks for another reload, for ever.
 *
 * So the reloads are counted. Past a few in a minute this stops reloading
 * and says so, because a bar the user can read beats a page that will not
 * settle.
 */
const RELOADS_KEY = 'colplat:sw-reloads';
const RELOAD_LIMIT = 3;

/**
 * This load works — so forget the reloads that got us here.
 *
 * The counter exists to stop a loop, and a loop is reloads that do not fix
 * anything. Once the app is actually running and talking to the server, they
 * plainly did, and keeping the tally punishes the next perfectly ordinary
 * update by making it ask permission — which is the thing the silent path
 * exists to avoid. Reload, see "a new version is ready, Reload", exactly as
 * before.
 */
export function noteHealthyLoad(): void {
  try {
    sessionStorage.removeItem(RELOADS_KEY);
  } catch { /* nothing to forget. */ }
}

function recordReloadAttempt(): number {
  // Every read and write guarded: `sessionStorage` throws in some privacy
  // modes, and a counter that cannot be kept must not take the app down.
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(RELOADS_KEY);
    const recent = (raw ? JSON.parse(raw) as number[] : []).filter((t) => now - t < 60_000);
    recent.push(now);
    sessionStorage.setItem(RELOADS_KEY, JSON.stringify(recent));
    return recent.length;
  } catch {
    return 1;
  }
}

/** Clean slate: drop the cached shell and the worker holding it. */
async function discardAppShell(): Promise<void> {
  try {
    const names = await caches.keys();
    await Promise.all(names
      .filter((name) => name.startsWith('colplat-shell-'))
      .map((name) => caches.delete(name)));
  } catch { /* nothing cached, or no access — the reload below still helps. */ }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.unregister();
  } catch { /* same. */ }
}

/**
 * Whether this update can simply be taken, without asking.
 *
 * The prompt exists because this app holds leases, half-typed split tables
 * and an outbox, and reloading underneath someone would throw live work
 * away. None of that exists on a page nobody has touched yet — and the
 * commonest way to meet this prompt is to press reload yourself, be served
 * the cached shell, and be asked a moment later to reload again. Answering a
 * question the user has already answered with their own hands is the wrong
 * way round.
 *
 * So: untouched, and new within moments of arriving. Any keystroke or
 * pointer press means there may be something on screen worth keeping, and
 * from then on it is the bar as before. An outbox entry is durable in
 * IndexedDB and survives either path; what cannot survive is a form nobody
 * has submitted, which is exactly what interaction stands for.
 */
function canUpdateSilently(): boolean {
  return !interacted && Date.now() - loadedAt < SILENT_UPDATE_WINDOW_MS;
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // A worker persists per scope, so it must not be installed by a page served
  // from a different mount path than the one it would claim.
  const scope = appHref('/') || '/';
  const scriptUrl = appHref('/sw.js');

  // Anything the user does means there may be work on screen worth keeping.
  // `once` on each: the first one settles it, and they cost nothing after.
  for (const event of ['pointerdown', 'keydown'] as const) {
    window.addEventListener(event, () => { interacted = true; }, { once: true, capture: true });
  }

  window.addEventListener('load', () => {
    void (async () => {
      try {
        const registration = await navigator.serviceWorker.register(scriptUrl, { scope });

        // Already waiting from a previous visit.
        if (registration.waiting && navigator.serviceWorker.controller) {
          onUpdateReady(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install — that is not an
            // update, it is this page becoming offline-capable, and there is
            // nothing to prompt about.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              onUpdateReady(installing);
            }
          });
        });
      } catch (err) {
        /*
         * No offline shell this session. Everything else still works — the
         * shell cache is an enhancement, not a dependency — so this is not
         * raised to the user.
         *
         * It IS said out loud, though. A bare `catch {}` here hid the fact
         * that registration fails outright in some browsers, which made
         * every question about the update prompt harder to answer than it
         * needed to be: "there is no worker" and "the worker is misbehaving"
         * look identical from outside, and only one of them is a bug in this
         * file.
         */
        console.warn('[colplat] offline shell unavailable:', err);
      }
    })();
  });

  // Fires once the new worker has taken over, whether the user accepted the
  // prompt or `forceUpdateNow()` was called.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

function onUpdateReady(worker: ServiceWorker): void {
  waitingWorker = worker;
  // Counted like any other self-reload: taking updates silently must not be
  // able to spin either, however it came to find a worker waiting each time.
  if (canUpdateSilently() && recordReloadAttempt() <= RELOAD_LIMIT) {
    // `controllerchange` reloads the page once this takes over.
    worker.postMessage({ t: 'skipWaiting' });
    return;
  }
  renderUpdateBar();
}

/**
 * Activate the waiting worker immediately, discarding nothing of the user's
 * making. Called on `schemaStale` (ADR-0016): `sync` has refused this client
 * for being too old to write safely, so a cache-first shell would otherwise
 * pin the user to a bundle the server will not talk to — permanently broken
 * rather than merely stale. That is the one case where reloading unasked is
 * the kinder outcome.
 */
/**
 * Take the new version now, by whatever means there is.
 *
 * A waiting worker is activated; with none, the shell it would have replaced
 * is thrown away and the page reloaded, which forces the next load to the
 * network. No tally and no conditions — this is what a person pressing
 * "Reload" is asking for, and it has to do something every time.
 *
 * The button used to post to the waiting worker and stop. With no worker
 * waiting — which is precisely the case the bar now appears in — it posted
 * to nothing and the page sat there. A control that does nothing is worse
 * than no control: it tells the reader the app is broken in a way they
 * cannot get out of.
 */
export function takeUpdateNow(): void {
  if (waitingWorker) {
    waitingWorker.postMessage({ t: 'skipWaiting' });
    return;
  }
  void (async () => {
    await discardAppShell();
    window.location.reload();
  })();
}

export function forceUpdateNow(): void {
  /*
   * A waiting worker is tried FIRST, and the tally does not gate it.
   *
   * Activating one is the thing that actually replaces the code, and it is
   * not what loops — a reload into the same cached shell is. Checking the
   * limit first meant that a browser which had already reloaded a few times
   * was shown a bar asking it to reload, while the worker that would have
   * fixed it sat waiting, untouched. Exactly the wrong way round: the user
   * had to press a button to do the one thing we could have done ourselves.
   */
  if (waitingWorker) {
    waitingWorker.postMessage({ t: 'skipWaiting' });
    return;
  }
  if (recordReloadAttempt() > RELOAD_LIMIT) {
    // Nothing to activate, and reloading by itself has not helped. Stop
    // doing it unprompted and offer it instead — the button discards the
    // shell, which an automatic reload has evidently not been enough to fix.
    renderUpdateBar('This app needs a new version. Reloading has not picked '
      + 'it up on its own — this button clears the stored copy and fetches it.');
    return;
  }
  // No worker waiting, so the stale bundle is the cached shell itself, and a
  // plain reload is answered from that same cache. Throw it away first.
  takeUpdateNow();
}

/**
 * A quiet, dismissible bar rather than a modal. Text plus a real button, in
 * an `aria-live="polite"` region — a new version being available is
 * information, never an interruption (invariant #11).
 */
function renderUpdateBar(message = 'A new version of this app is ready.'): void {
  if (document.getElementById('cfUpdateBar')) return;
  const bar = document.createElement('div');
  bar.id = 'cfUpdateBar';
  bar.className = 'cf-updatebar';
  bar.setAttribute('role', 'status');
  bar.setAttribute('aria-live', 'polite');
  bar.innerHTML =
    `<span>${message}</span>` +
    '<button type="button" class="btn btn-sm btn-primary" id="cfUpdateReload">Reload</button>' +
    '<button type="button" class="btn btn-sm btn-link" id="cfUpdateLater">Later</button>';
  document.body.append(bar);

  document.getElementById('cfUpdateReload')?.addEventListener('click', () => {
    takeUpdateNow();
  });
  document.getElementById('cfUpdateLater')?.addEventListener('click', () => {
    bar.remove();
  });
}
